import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import WorkloadPanel from '../components/admin/WorkloadPanel';
import TemplatesPanel from '../components/admin/TemplatesPanel';
import MetricsPanel from '../components/admin/MetricsPanel';
import SupervisionPanel from '../components/admin/SupervisionPanel';
import PromotionsPanel from '../components/admin/PromotionsPanel';
import ThemeToggle from '../theme/ThemeToggle';
import { ArrowLeft, Users, ListChecks, FileText, BarChart3, Radar, Megaphone } from 'lucide-react';

const ROLE_LABEL = { admin: 'Admin', agent: 'Asesor' };

export default function AdminPage({ onBack, initialTab, onOpenStudio }) {
  const { user: me } = useAuth();
  const { apiFetch } = useApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Contraseña temporal recién generada: se muestra una sola vez
  const [secret, setSecret] = useState(null);
  const [tab, setTab] = useState(initialTab || 'users'); // users | workload | templates | promotions | metrics | supervision
  // Nombre de cada tipo de asesor según el cliente (p. ej. { balancer: 'Técnico', transfer: 'Extensión' })
  const [agentTypes, setAgentTypes] = useState({});

  useEffect(() => {
    apiFetch('/api/admin/agent-types').then(setAgentTypes).catch(() => {});
  }, [apiFetch]);

  const load = useCallback(async () => {
    try {
      setUsers(await apiFetch('/api/admin/users'));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  async function update(u, changes, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const data = await apiFetch(`/api/admin/users/${u.id}`, { method: 'PUT', body: JSON.stringify(changes) });
      await load();
      if (data.requeued) alert(`${data.requeued} chat(s) de ${u.name} volvieron a la cola y se reparten entre los asesores disponibles.`);
    } catch (e) {
      alert(e.message);
    }
  }

  async function resetPassword(u) {
    if (!window.confirm(`¿Restablecer la contraseña de ${u.name}? Se cerrarán sus sesiones abiertas.`)) return;
    try {
      const data = await apiFetch(`/api/admin/users/${u.id}/reset-password`, { method: 'POST' });
      setSecret({ name: u.name, email: u.email, password: data.temp_password });
      await load();
    } catch (e) {
      alert(e.message);
    }
  }

  return (
    <div style={s.root}>
      <header style={s.header}>
        <div style={s.headerTop}>
          <button className="ui-hover" onClick={onBack} style={s.backBtn}><ArrowLeft size={15} /> Volver al chat</button>
          <h1 style={s.title}>Administración</h1>
          <div style={{ flex: 1 }} />
          <ThemeToggle style={s.iconBtn} />
        </div>
        <nav style={s.tabs} aria-label="Secciones de administración">
          {[
            ['users', 'Usuarios', Users], ['workload', 'Cola y carga', ListChecks], ['templates', 'Plantillas', FileText],
            ['promotions', 'Promociones', Megaphone],
            ['metrics', 'Métricas', BarChart3], ['supervision', 'Supervisión', Radar],
          ].map(([key, label, Icon]) => (
            <button key={key} style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }} onClick={() => setTab(key)}
              aria-current={tab === key ? 'page' : undefined}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </nav>
      </header>

      {tab === 'workload' ? (
        <main style={s.main}><WorkloadPanel /></main>
      ) : tab === 'templates' ? (
        <main style={s.main}><TemplatesPanel /></main>
      ) : tab === 'promotions' ? (
        <main style={s.main}><PromotionsPanel /></main>
      ) : tab === 'metrics' ? (
        <main style={s.main}><MetricsPanel /></main>
      ) : tab === 'supervision' ? (
        <main style={s.main}><SupervisionPanel onOpenStudio={onOpenStudio} /></main>
      ) : (
      <main style={s.main}>
        {secret && (
          <div style={s.secretBox}>
            <div>
              <strong>Contraseña temporal de {secret.name}</strong> ({secret.email}):
              <code style={s.code}>{secret.password}</code>
            </div>
            <div style={s.secretHint}>
              Entrégala por un canal privado. No se volverá a mostrar y deberá cambiarla al ingresar.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.smallBtn} onClick={() => navigator.clipboard?.writeText(secret.password)}>Copiar</button>
              <button style={s.smallBtn} onClick={() => setSecret(null)}>Listo</button>
            </div>
          </div>
        )}

        <CreateUserForm apiFetch={apiFetch} agentTypes={agentTypes} onCreated={(u, temp) => {
          if (temp) setSecret({ name: u.name, email: u.email, password: temp });
          load();
        }} />

        {error && <div style={s.errorBox}>⚠️ {error}</div>}

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Nombre</th>
                <th style={s.th}>Correo</th>
                <th style={s.th}>Rol</th>
                <th style={s.th} title="Quién entra al reparto automático y quién solo recibe chats transferidos">Tipo</th>
                <th style={s.th} title="Desmárcalo en vacaciones o día libre: no se le asigna ni transfiere nada y sus chats abiertos vuelven a la cola">Recibe casos</th>
                <th style={s.th}>Estado</th>
                <th style={s.th}>Último ingreso</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td style={s.td} colSpan={8}>Cargando...</td></tr>}
              {users.map(u => {
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                    <td style={s.td}>{u.name}{isMe && <span style={s.meTag}>tú</span>}</td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}>
                      <select
                        style={s.select}
                        value={u.role}
                        disabled={isMe}
                        onChange={e => update(u, { role: e.target.value },
                          `¿Cambiar el rol de ${u.name} a ${ROLE_LABEL[e.target.value]}? Se cerrarán sus sesiones.`)}
                      >
                        <option value="agent">Asesor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td style={s.td}>
                      <select
                        style={s.select}
                        value={u.agent_type}
                        onChange={e => update(u, { agent_type: e.target.value })}
                      >
                        <AgentTypeOptions agentTypes={agentTypes} />
                      </select>
                    </td>
                    <td style={s.td}>
                      <input
                        type="checkbox"
                        checked={u.receives_assignments}
                        onChange={e => update(u, { receives_assignments: e.target.checked }, e.target.checked ? null :
                          `¿Quitarle "Recibe casos" a ${u.name}? No se le asignarán ni transferirán casos y sus chats abiertos volverán a la cola.`)}
                      />
                    </td>
                    <td style={s.td}>
                      {u.is_active
                        ? (u.must_change_password ? <span style={s.pending}>Clave pendiente</span> : <span style={s.active}>Activo</span>)
                        : <span style={s.inactive}>Inactivo</span>}
                    </td>
                    <td style={s.td}>
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      {!isMe && (
                        <>
                          <button style={s.smallBtn} onClick={() => resetPassword(u)}>Restablecer clave</button>{' '}
                          {u.is_active
                            ? <button style={s.dangerBtn} onClick={() => update(u, { is_active: false },
                                `¿Desactivar a ${u.name}? Perderá el acceso de inmediato.`)}>Desactivar</button>
                            : <button style={s.smallBtn} onClick={() => update(u, { is_active: true })}>Activar</button>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
      )}
    </div>
  );
}

/** Opciones del tipo de asesor, con el nombre del cliente (o el id mientras carga). */
function AgentTypeOptions({ agentTypes }) {
  return ['balancer', 'transfer'].map(t => <option key={t} value={t}>{agentTypes[t] || t}</option>);
}

function CreateUserForm({ apiFetch, agentTypes, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('agent');
  const [agentType, setAgentType] = useState('balancer');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      // Sin contraseña: el backend genera una temporal y obliga a cambiarla
      const data = await apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ name, email, role, agent_type: agentType }),
      });
      setName(''); setEmail(''); setRole('agent'); setAgentType('balancer');
      onCreated(data.user, data.temp_password);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={s.createForm}>
      <input style={s.input} placeholder="Nombre" value={name} onChange={e => setName(e.target.value)} required />
      <input style={s.input} placeholder="Correo" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
      <select style={s.select} value={role} onChange={e => setRole(e.target.value)}>
        <option value="agent">Asesor</option>
        <option value="admin">Admin</option>
      </select>
      <select style={s.select} value={agentType} onChange={e => setAgentType(e.target.value)} aria-label="Tipo de asesor">
        <AgentTypeOptions agentTypes={agentTypes} />
      </select>
      <button type="submit" style={s.primaryBtn} disabled={saving}>{saving ? 'Creando...' : '+ Crear usuario'}</button>
      {error && <div style={{ ...s.errorBox, width: '100%' }}>⚠️ {error}</div>}
    </form>
  );
}

const s = {
  root: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  header: { position: 'sticky', top: 0, zIndex: 10, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '14px 24px 0' },
  headerTop: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 },
  title: { fontSize: 20, fontWeight: 800, color: 'var(--text)', margin: 0, letterSpacing: -0.3 },
  tabs: { display: 'flex', gap: 4, overflowX: 'auto' },
  tab: { display: 'inline-flex', alignItems: 'center', gap: 7, background: 'transparent', border: 'none', borderBottom: '2px solid transparent', padding: '9px 12px 11px', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  tabActive: { color: 'var(--accent-text)', borderBottom: '2px solid var(--accent)' },
  iconBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '7px 12px', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  main: { padding: 24, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1240, margin: '0 auto', width: '100%' },
  createForm: {
    display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14,
  },
  input: {
    background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8,
    padding: '8px 12px', color: 'var(--text)', fontSize: 13, outline: 'none', minWidth: 200,
  },
  select: {
    background: 'var(--raised)', border: '1px solid var(--border)', borderRadius: 8,
    padding: '7px 10px', color: 'var(--text)', fontSize: 13,
  },
  primaryBtn: {
    background: 'var(--accent)', border: 'none', borderRadius: 10,
    padding: '9px 16px', color: 'var(--on-accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
  smallBtn: {
    background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 6,
    padding: '5px 10px', color: 'var(--text-2)', cursor: 'pointer', fontSize: 12,
  },
  dangerBtn: {
    background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
    padding: '5px 10px', color: 'var(--danger)', cursor: 'pointer', fontSize: 12,
  },
  tableWrap: { overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 12px', color: 'var(--text-2)', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '1px solid var(--border)',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)' },
  meTag: {
    marginLeft: 6, fontSize: 10, background: 'var(--accent-soft)', color: 'var(--accent-text)',
    borderRadius: 4, padding: '1px 5px',
  },
  active: { color: 'var(--success)' },
  inactive: { color: 'var(--text-3)' },
  pending: { color: 'var(--warning)' },
  errorBox: {
    background: 'var(--danger-soft)', border: '1px solid var(--danger-border)',
    borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13,
  },
  secretBox: {
    display: 'flex', flexDirection: 'column', gap: 8,
    background: 'var(--success-soft)', border: '1px solid var(--success-border)',
    borderRadius: 12, padding: 14, fontSize: 13,
  },
  secretHint: { color: 'var(--text-2)', fontSize: 12 },
  code: {
    marginLeft: 8, background: 'var(--surface-2)', padding: '3px 8px', borderRadius: 6,
    fontFamily: 'monospace', fontSize: 14, color: 'var(--success)', userSelect: 'all',
  },
};
